/* Order tracking - Shopify Admin + local backend lookup */
(() => {
  const form = document.querySelector("[data-track-form]");
  const result = document.querySelector("[data-track-result]");
  const content = document.querySelector("[data-track-content]");
  const errorEl = document.querySelector("[data-track-error]");
  const submitBtn = document.querySelector("[data-track-submit]");
  if (!form || !result || !content) return;

  const STATUS_LABEL = {
    confirmed: "Confirmed",
    packed: "Packed",
    out_for_delivery: "Out for delivery",
    delivered: "Delivered",
    cancelled: "Cancelled"
  };
  const STATUS_ORDER = ["confirmed", "packed", "out_for_delivery", "delivered"];
  const POLL_MS = 5000;

  let activeQuery = null;
  let lastUpdatedAt = null;
  let pollTimer = 0;
  let firstPaint = true;

  function escapeHtml(str) {
    return String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function normalizePhone(phone) {
    return String(phone || "").replace(/\D/g, "").replace(/^92/, "0");
  }

  function normalizeOrder(id) {
    return String(id || "")
      .trim()
      .toUpperCase()
      .replace(/^#/, "")
      .replace(/^ORDER\s+/i, "")
      .replace(/\s+/g, "");
  }

  function formatPhone(phone) {
    const d = normalizePhone(phone);
    if (d.length === 11) return `${d.slice(0, 4)} ${d.slice(4, 7)} ${d.slice(7)}`;
    return phone;
  }

  function setError(msg) {
    if (!errorEl) return;
    if (!msg) {
      errorEl.hidden = true;
      errorEl.textContent = "";
      return;
    }
    errorEl.hidden = false;
    errorEl.textContent = msg;
  }

  function setLoading(on) {
    if (!submitBtn) return;
    submitBtn.disabled = !!on;
    submitBtn.textContent = on ? "Tracking…" : "Track order";
  }

  async function lookup(order, phone) {
    const qs = new URLSearchParams({ order, phone });
    const urls = [
      `/api/track?${qs.toString()}`,
      `https://mannacrockery.pk/api/track?${qs.toString()}`
    ];
    let lastErr = null;
    for (const url of urls) {
      try {
        const res = await fetch(url, {
          headers: { Accept: "application/json" },
          cache: "no-store"
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          lastErr = new Error(data.error || "Order not found");
          lastErr.code = res.status;
          continue;
        }
        return data;
      } catch (err) {
        lastErr = err;
      }
    }
    const err = lastErr || new Error("Tracking unavailable");
    err.hint = "WhatsApp us on 0311-4711112 with your order number, or sign in to your account.";
    throw err;
  }

  function stepClass(step, currentStatus) {
    if (currentStatus === "cancelled") {
      return step.key === "confirmed" || step.key === "cancelled" ? "is-done" : "is-pending";
    }
    const stepIdx = STATUS_ORDER.indexOf(step.key);
    const curIdx = STATUS_ORDER.indexOf(currentStatus);
    if (stepIdx < 0 || curIdx < 0) return "is-pending";
    if (stepIdx < curIdx) return "is-done";
    if (stepIdx === curIdx) return "is-current";
    return "is-pending";
  }

  function renderOrder(order, { silent } = {}) {
    const statusLabel = STATUS_LABEL[order.status] || order.status;
    const timelineSteps = order.timeline || [];

    const timeline = timelineSteps.map((step) => {
      const state = stepClass(step, order.status);
      const timeLabel = step.at || "Pending";
      return `<li class="track-step ${state}">
        <div class="track-step__rail" aria-hidden="true"><span class="track-step__dot"></span></div>
        <div class="track-step__body">
          <p class="track-step__label">${escapeHtml(step.label)}</p>
          <span class="track-step__time">${escapeHtml(timeLabel)}</span>
          <p class="track-step__note">${escapeHtml(step.note || "")}</p>
        </div>
      </li>`;
    }).join("");

    const items = (order.items || []).map((item) => `<li>
      <span><strong>${escapeHtml(item.title)}</strong> · Qty ${escapeHtml(item.qty)}</span>
      <span>${escapeHtml(item.price || "")}</span>
    </li>`).join("");

    const waText = encodeURIComponent(`Hello Manna Crockery - I need help with order ${order.id}`);
    const liveNote = (order.status === "delivered" || order.status === "cancelled")
      ? ""
      : `<p class="track-live" data-track-live>Live · updates automatically when status changes</p>`;

    const sourceBadge = order.source === "shopify"
      ? `<span class="track-source" title="Live from Shopify">Shopify</span>`
      : "";

    const trackLink = order.trackingUrl
      ? `<a class="btn btn-dark" href="${escapeHtml(order.trackingUrl)}" target="_blank" rel="noopener noreferrer">Open courier tracking</a>`
      : "";

    const paymentLabel = order.financialStatus
      ? String(order.financialStatus).replace(/_/g, " ")
      : (order.payment || "-");

    content.innerHTML = `
      <div class="track-result__head">
        <div>
          <h2>Order ${escapeHtml(order.id)} ${sourceBadge}</h2>
          <p class="track-result__meta">${escapeHtml(order.customer)} · ${escapeHtml(order.city || "Pakistan")} · ETA ${escapeHtml(order.eta || "-")}</p>
          ${liveNote}
        </div>
        <span class="track-badge" data-status="${escapeHtml(order.status)}">${escapeHtml(statusLabel)}</span>
      </div>
      <div class="track-grid">
        <ol class="track-timeline">${timeline}</ol>
        <aside class="track-summary">
          <h3>Shipment details</h3>
          <ul class="track-summary__list">
            <li><strong>Courier</strong><span>${escapeHtml(order.courier || "-")}</span></li>
            <li><strong>Tracking</strong><span>${escapeHtml(order.trackingCode || "-")}</span></li>
            <li><strong>Payment</strong><span>${escapeHtml(paymentLabel)}</span></li>
            <li><strong>Phone</strong><span>${escapeHtml(formatPhone(order.phone))}</span></li>
            <li><strong>Updated</strong><span>${escapeHtml(order.timeline?.find((s) => s.key === order.status)?.at || "-")}</span></li>
          </ul>
          <ul class="track-items">${items}</ul>
          <div class="track-summary__actions">
            ${trackLink}
            <a class="btn btn-gold" href="https://wa.me/923114711112?text=${waText}" target="_blank" rel="noopener noreferrer">Ask about this order</a>
            <a class="btn btn-dark" href="/collections/all">Continue shopping</a>
          </div>
        </aside>
      </div>`;
    result.hidden = false;
    if (!silent || firstPaint) {
      result.scrollIntoView({ behavior: "smooth", block: "start" });
      firstPaint = false;
    }
  }

  function renderEmpty(orderId) {
    stopPoll();
    const waText = encodeURIComponent(`Hello Manna Crockery - I can't find order ${orderId || ""}`);
    content.innerHTML = `
      <div class="track-empty">
        <h2>We couldn’t find that order</h2>
        <p>Use your Shopify order number (e.g. <strong>#1042</strong>) or demo ID (<strong>MC-10482</strong>) with the same phone used at checkout.</p>
        <div class="track-empty__actions">
          <a class="btn btn-gold" href="https://wa.me/923114711112?text=${waText}" target="_blank" rel="noopener noreferrer">WhatsApp support</a>
          <a class="btn btn-dark" href="/pages/contact">Contact us</a>
        </div>
      </div>`;
    result.hidden = false;
    result.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function stopPoll() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = 0;
    }
  }

  function startPoll() {
    stopPoll();
    if (!activeQuery) return;
    pollTimer = window.setInterval(async () => {
      if (document.hidden || !activeQuery) return;
      try {
        const data = await lookup(activeQuery.order, activeQuery.phone);
        if (data.updatedAt !== lastUpdatedAt) {
          lastUpdatedAt = data.updatedAt;
          renderOrder(data, { silent: true });
          const live = content.querySelector("[data-track-live]");
          if (live) {
            live.textContent = "Updated just now";
            window.setTimeout(() => {
              if (live.isConnected) live.textContent = "Live · updates automatically when status changes";
            }, 2500);
          }
        }
        if (data.status === "delivered" || data.status === "cancelled") stopPoll();
      } catch (_) {
        /* keep last good state while polling */
      }
    }, POLL_MS);
  }

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    setError("");
    const orderEl = form.elements.namedItem("order");
    const phoneEl = form.elements.namedItem("phone");
    const order = normalizeOrder(orderEl && orderEl.value);
    const phone = normalizePhone(phoneEl && phoneEl.value);

    orderEl?.classList.toggle("is-invalid", !order);
    phoneEl?.classList.toggle("is-invalid", phone.length < 10);

    if (!order || phone.length < 10) {
      setError("Enter a valid order number and phone.");
      return;
    }

    setLoading(true);
    firstPaint = true;
    try {
      const data = await lookup(order, phone);
      activeQuery = { order, phone };
      lastUpdatedAt = data.updatedAt;
      renderOrder(data);
      if (data.status !== "delivered" && data.status !== "cancelled") startPoll();
      else stopPoll();
      const url = new URL(window.location.href);
      url.searchParams.set("order", order);
      url.searchParams.set("phone", phone);
      history.replaceState({}, "", url);
    } catch (err) {
      activeQuery = null;
      lastUpdatedAt = null;
      stopPoll();
      if (err.code === 404) renderEmpty(order);
      else setError(err.message || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  });

  document.querySelectorAll("[data-sample]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const [order, phone] = String(btn.dataset.sample || "").split("|");
      const orderEl = form.elements.namedItem("order");
      const phoneEl = form.elements.namedItem("phone");
      if (orderEl) orderEl.value = order;
      if (phoneEl) phoneEl.value = phone;
      form.requestSubmit();
    });
  });

  document.addEventListener("visibilitychange", () => {
    if (!document.hidden && activeQuery) startPoll();
  });

  const params = new URLSearchParams(window.location.search);
  const qOrder = params.get("order");
  const qPhone = params.get("phone");
  if (qOrder && qPhone) {
    const orderEl = form.elements.namedItem("order");
    const phoneEl = form.elements.namedItem("phone");
    if (orderEl) orderEl.value = qOrder;
    if (phoneEl) phoneEl.value = qPhone;
    form.requestSubmit();
  }
})();
